import { Html5Qrcode } from "html5-qrcode";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import UploadIcon from "@mui/icons-material/Upload";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CameraIcon from "@mui/icons-material/Camera";
import MDTypography from "../MDTypography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const ERROR_MESSAGES = {
  1: "Erreur.",
  2: "Format de fichier invalide.",
  3: "Impossible de démarrer l'analyse de code QR.",
  4: "Aucun code QR trouvé dans l'image.",
};

const QRCodeScanner = ({ mode = "qrcode", onResult, disabled, className, onCancel }) => {
  const [devices, setDevices] = useState([]);
  const [scanId, setScanId] = useState(null);
  const [error, setError] = useState({ open: false });
  const html5Qrcode = useRef();

  useEffect(() => {
    if (mode === "qrcode" || mode === "file") {
      Html5Qrcode.getCameras()
        .then((devices) => {
          setDevices(devices);
          html5Qrcode.camera = 0;
        })
        .catch((err) => {
          // Permission not given
        });
    }
  }, [mode]);

  const setQrBox =
    mode === "file"
      ? null
      : (viewfinderWidth, viewfinderHeight) => ({
          width: Math.round(0.7 * viewfinderWidth),
          height: Math.round(0.7 * viewfinderHeight),
        });

  useEffect(() => {
    if (scanId) {
      html5Qrcode.current = new Html5Qrcode(scanId);
      html5Qrcode.current
        .start(
          devices[html5Qrcode.camera].id,
          { fps: 10, qrbox: setQrBox },
          handleResult,
          (_) => {}
        )
        .catch((err) => {
          setError({ open: true, code: 3 });
        });

      // cleanup function when component will unmount
      return () => {
        html5Qrcode.current
          .stop()
          .then(() => {
            html5Qrcode.current.clear();
            html5Qrcode.current = null;
          })
          .catch((error) => {
            console.error("Failed to clear html5QrcodeScanner. ", error);
          });
      };
    }
  }, [scanId]);

  const handleResult = (result) => {
    setScanId(null);

    onResult(result);
  };

  const handleFileUpload = (e) => {
    if (e.currentTarget && e.currentTarget.files && e.currentTarget.files.length) {
      const file = e.currentTarget.files[0];
      e.currentTarget.value = null;
      if (mode === "file") {
        handleResult(file);
      } else {
        if (file.type.startsWith("image/")) {
          // Load image
          const inputImage = new Image();
          inputImage.onload = function () {
            // Create a temporary rendering div
            const div = document.createElement("div");
            const id = "reader" + Math.round(Math.random() * 100000 + 100000);
            div.setAttribute("id", id);
            div.style.position = "absolute";
            div.style.top = "0px";
            div.style.left = "0px";
            div.style.width = inputImage.naturalWidth + "px";
            div.style.height = inputImage.naturalHeight + "px";
            div.style.opacity = "0";
            document.body.appendChild(div);

            // Create a reader and decode file
            const html5QrCode = new Html5Qrcode(id);
            html5QrCode
              .scanFileV2(file, true)
              .then((result) => {
                document.body.removeChild(div);
                handleResult(result.decodedText);
              })
              .catch((err) => {
                setError({ open: true, code: 4 });
                document.body.removeChild(div);
              });
          };
          inputImage.src = URL.createObjectURL(file);
        } else {
          setError({ open: true, code: 2 });
        }
      }
    }
  };

  const switchCamera = () => {
    if (html5Qrcode.current) {
      html5Qrcode.current.stop().then((_) => {
        html5Qrcode.camera = (html5Qrcode.camera + 1) % devices.length;
        html5Qrcode.current
          .start(
            devices[html5Qrcode.camera].id,
            { fps: 10, qrbox: setQrBox },
            handleResult,
            (_) => {}
          )
          .catch((err) => {
            setError({ open: true, code: 3 });
          });
      });
    }
  };

  const stopCamera = () => {
    setScanId(null);
  };

  const startScan = () => {
    setScanId("qr-" + Math.floor(Math.random() * 1000000 + 1000000));
  };

  const takePhoto = () => {
    if (html5Qrcode.current) {
      const videoElement = html5Qrcode.current.renderedCamera.getSurface();
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      const canvas = document.createElement("canvas");
      canvas.width = videoWidth;
      canvas.height = videoHeight;

      const context = canvas.getContext("2d");
      context.drawImage(videoElement, 0, 0, videoWidth, videoHeight, 0, 0, videoWidth, videoHeight);
      canvas.toBlob(
        (blob) => {
          const date = new Date();
          const pad = (x) => (x < 10 ? "0" : "") + x;
          handleResult(
            new File(
              [blob],
              "photo-" +
                ["getFullYear", "getMonth", "getDate", "getHours", "getMinutes", "getSeconds"]
                  .map((k) => pad(date[k]() + (k === "getMonth" ? 1 : 0)))
                  .join("") +
                ".png",
              { type: "image/png" }
            )
          );
        },
        "image/png",
        1
      );
    }
  };

  const closeError = () => setError((e) => ({ ...e, open: false }));

  const ScanIcon = mode === "file" ? PhotoCameraIcon : QrCodeScannerIcon;

  return (
    <div style={{ position: "relative", paddingBottom: "75%" }} className={className}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          border: "1px solid silver",
          borderRadius: 8,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
        }}
      >
        {!scanId && (
          <>
            <div
              style={{
                border: "1px solid silver",
                padding: 16,
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                position: "relative",
                opacity: disabled ? 0.3 : 1,
                pointerEvents: disabled ? "none" : "inherit",
              }}
            >
              <UploadIcon sx={{ width: 64, height: 64, mb: 1 }} />
              <MDTypography component="div" variant="caption">
                Utiliser un fichier
              </MDTypography>
              <input
                type="file"
                onChange={handleFileUpload}
                style={{
                  position: "absolute",
                  zIndex: 1,
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </div>
            <div
              style={{
                border: "1px solid silver",
                padding: 16,
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                opacity: !devices.length || disabled ? 0.3 : 1,
                pointerEvents: !devices.length || disabled ? "none" : "inherit",
              }}
              onClick={startScan}
            >
              <ScanIcon sx={{ width: 64, height: 64, mb: 1 }} />
              <MDTypography component="div" variant="caption">
                {mode === "file" ? "Prendre une photo" : "Scanner un code"}
              </MDTypography>
            </div>
            {!!onCancel && (
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  border: "1px solid silver",
                  backgroundColor: "white",
                  cursor: "pointer",
                  borderRadius: "50%",
                  height: 48,
                  width: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={onCancel}
              >
                <CloseIcon sx={{ height: 32, width: 32 }} />
              </div>
            )}
          </>
        )}
        {scanId && (
          <>
            <div style={{ width: "100%" }} id={scanId} />
            {devices.length > 1 && (
              <div
                style={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  border: "1px solid silver",
                  backgroundColor: "white",
                  cursor: "pointer",
                  borderRadius: "50%",
                  height: 48,
                  width: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={switchCamera}
              >
                <CameraswitchIcon sx={{ height: 32, width: 32 }} />
              </div>
            )}
            {mode === "file" && (
              <div
                style={{
                  position: "absolute",
                  bottom: 4,
                  left: "50%",
                  marginLeft: -24,
                  border: "1px solid silver",
                  backgroundColor: "white",
                  cursor: "pointer",
                  borderRadius: "50%",
                  height: 48,
                  width: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={takePhoto}
              >
                <CameraIcon sx={{ color: "red", height: 32, width: 32 }} />
              </div>
            )}
            <div
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                border: "1px solid silver",
                backgroundColor: "white",
                cursor: "pointer",
                borderRadius: "50%",
                height: 48,
                width: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={stopCamera}
            >
              <CloseIcon sx={{ height: 32, width: 32 }} />
            </div>
          </>
        )}
      </div>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={closeError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {ERROR_MESSAGES[error.code] || ERROR_MESSAGES[1]}
        </Alert>
      </Snackbar>
    </div>
  );
};

QRCodeScanner.propTypes = {
  className: PropTypes.string,
  mode: PropTypes.string,
  onResult: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
};

export default QRCodeScanner;
